import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Modal from 'common-ui/modal';
import Pagination from 'common-ui/table/Pagination';
import {
  createLeadSource,
  deleteLeadSource,
  getLeadSources,
  updateLeadSource,
} from 'features/newsales/newsalesActions';
import {
  selectLeadSources,
  selectLeadSourcesCurrentPage,
  selectLeadSourcesDeleteSuccess,
  selectLeadSourcesLoading,
  selectLeadSourcesPagesCount,
  selectLeadSourcesSearchData,
  selectLeadSourcesSuccess,
  selectLeadSourcesTotalCount,
  selectLeadSourcesUpdateSuccess,
  setLeadSourcesCreateSuccess,
  setLeadSourcesCurrentPage,
  setLeadSourcesDeleteSuccess,
  setLeadSourcesSearchData,
  setLeadSourcesUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import LeadSourceCard from '../components/LeadSourceCard';
import LeadSourceImage from '../components/LeadSourceImage';
import LeadSourceModal from '../components/LeadSourceModal';
import SearchFilter from '../components/SearchFilter';
import {
  CardsGrid,
  EmptyState,
  LeadSourcesContainer,
  LeadSourcesContent,
  LeadSourcesTitle,
  LoadingSpinner,
  PaginationWrapper,
} from './LeadSources.styles';

const LeadSources = () => {
  const dispatch = useDispatch();

  const leadSources = useSelector(selectLeadSources);
  const loading = useSelector(selectLeadSourcesLoading);
  const currentPage = useSelector(selectLeadSourcesCurrentPage);
  const pagesCount = useSelector(selectLeadSourcesPagesCount);
  const searchData = useSelector(selectLeadSourcesSearchData);
  const createSuccess = useSelector(selectLeadSourcesSuccess);
  const deleteSuccess = useSelector(selectLeadSourcesDeleteSuccess);
  const updateSuccess = useSelector(selectLeadSourcesUpdateSuccess);
  const totalCount = useSelector(selectLeadSourcesTotalCount);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedSource, setSelectedSource] = useState(null);

  const resetData = () => {
    const params = {
      status: 'enabled',
      page: 1,
      limit: 12,
    };
    dispatch(getLeadSources(params));
  };

  useEffect(() => {
    const params = {
      status: searchData.status,
      page: currentPage,
      limit: searchData.limit,
      search: searchData.search,
    };
    dispatch(getLeadSources(params));
  }, [dispatch, currentPage, searchData.status, searchData.limit, searchData.search]);

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setSelectedSource(null);
      dispatch(setLeadSourcesCreateSuccess(false));
      resetData();
    }
  }, [createSuccess, dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setSelectedSource(null);
      dispatch(setLeadSourcesUpdateSuccess(false));
      resetData();
    }
  }, [updateSuccess, dispatch]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(setLeadSourcesDeleteSuccess(false));
      resetData();
    }
  }, [deleteSuccess, dispatch, leadSources?.length, currentPage, searchData]);

  const handleSearchChange = (value) => {
    dispatch(setLeadSourcesSearchData({ search: value }));
    dispatch(setLeadSourcesCurrentPage(1));
  };

  const handleStatusChange = (value) => {
    dispatch(setLeadSourcesSearchData({ status: value }));
    dispatch(setLeadSourcesCurrentPage(1));
  };

  const handlePageChange = (page) => {
    dispatch(setLeadSourcesCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setLeadSourcesSearchData({ limit: count }));
    dispatch(setLeadSourcesCurrentPage(1));
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedSource(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (id) => {
    const sourceToEdit = leadSources.find((source) => source.id === id);
    if (sourceToEdit) {
      setModalMode('edit');
      setSelectedSource(sourceToEdit);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSource(null);
  };

  const handleSubmit = (idOrData, leadSourceData) => {
    if (modalMode === 'create') {
      dispatch(createLeadSource(idOrData));
    } else {
      dispatch(updateLeadSource({ id: idOrData, data: leadSourceData }));
    }
  };

  const handleDeleteSource = (id) => {
    setSourceToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (sourceToDelete) {
      dispatch(deleteLeadSource(sourceToDelete));
    }
    setDeleteModalOpen(false);
    setSourceToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSourceToDelete(null);
  };

  return (
    <LeadSourcesContainer>
      <LeadSourcesTitle>Lead Sources</LeadSourcesTitle>
      <SearchFilter
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        searchPlaceholder="Search lead sources..."
        showCreateButton
        onCreateClick={handleOpenCreateModal}
        statusOptions={[
          { label: 'All', value: 'all' },
          { label: 'Active', value: 'enabled' },
          { label: 'Disabled', value: 'disabled' },
        ]}
      />
      <LeadSourcesContent>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : !leadSources || leadSources?.length === 0 ? (
          <EmptyState>No lead sources found</EmptyState>
        ) : (
          <>
            <CardsGrid>
              {leadSources.map((source) => (
                <LeadSourceCard
                  key={source.id}
                  icon={
                    <LeadSourceImage imagePath={source.image} alt={source.name} size={40} />
                  }
                  title={source.name}
                  status={source.enabled ? 'Enabled' : 'Disabled'}
                  description={source.description || 'No description'}
                  onEdit={() => handleOpenEditModal(source.id)}
                  onDelete={() => handleDeleteSource(source.id)}
                />
              ))}
            </CardsGrid>

            {leadSources?.length > 0 && (
              <PaginationWrapper>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagesCount}
                  onPageChange={handlePageChange}
                  handleRowCountChange={handleRowCountChange}
                  dataCount={totalCount}
                  count={searchData.limit}
                  editableRowCount
                />
              </PaginationWrapper>
            )}
          </>
        )}
      </LeadSourcesContent>

      <LeadSourceModal
        mode={modalMode}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCreate={handleSubmit}
        onUpdate={handleSubmit}
        initialData={selectedSource}
      />

      <Modal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onOk={handleConfirmDelete}
        title="Confirm Deletion"
        footer
        okText="Delete"
        cancelText="Cancel"
      >
        Are you sure you want to delete this source?
      </Modal>
    </LeadSourcesContainer>
  );
};

export default LeadSources;
